import { FormProvider } from 'react-hook-form';

import { ButtonType } from '@xemplo/button';
import { PayrunExtended } from '@xemplo/gp-types';
import { HeaderIconMode } from '@xemplo/header-icon';
import { DawnPencilEdit } from '@xemplo/icons';
import { Modal, ModalDialogSlot } from '@xemplo/modal-dialog';

import { getMilestoneGroups } from '../edit-payrun-milestones.const';
import * as S from '../edit-payrun-milestones.styles';
import { ModalDialogRef } from '../edit-payrun-milestones.types';
import { ValidationBanner } from '../validations/validation-banner';
import { WeekendWarningBanner } from '../validations/weekend-warning-banner';

import { MilestoneRow } from './milestone-row';
import { TimezoneBanner } from './timezone-banner';
import { useEditMilestonesForm } from './use-edit-milestones-form';

export type EditMilestonesContentProps = {
  modalDialog: ModalDialogRef;
  payrunId: string;
  payrun: PayrunExtended;
  onSuccess?: () => void;
};

export function EditMilestonesContent({
  modalDialog,
  payrunId,
  payrun,
  onSuccess,
}: Readonly<EditMilestonesContentProps>) {
  const { methods, handleCancel, handleSubmit } = useEditMilestonesForm({
    payrun,
    payrunId,
    modalDialog,
    onSuccess,
  });

  return (
    <Modal>
      <Modal.Slot
        name={ModalDialogSlot.CloseButton}
        render={
          <Modal.CloseButton
            onClick={handleCancel}
            id="edit-payrun-milestones-close-button"
            ariaLabel="Close edit milestones modal"
          />
        }
      />
      <Modal.Slot
        name={ModalDialogSlot.Header}
        render={
          <Modal.Header
            title="Edit Milestones"
            description="Finalising a pay run locks the pay run and prevents future edits"
            headerIcon={{ icon: <DawnPencilEdit />, mode: HeaderIconMode.Object }}
          />
        }
      />
      <Modal.Slot
        name={ModalDialogSlot.Body}
        render={
          <Modal.Body>
            <FormProvider {...methods}>
              <S.Body>
                <TimezoneBanner businessUnitId={payrun.businessUnitId} />
                {getMilestoneGroups(payrun.payrollModelId).map((group) => (
                  <S.Group key={group.title}>
                    <S.GroupTitle>{group.title}</S.GroupTitle>
                    <S.GroupCard>
                      {group.fields.map((field) => (
                        <MilestoneRow key={field.key} field={field} payrun={payrun} />
                      ))}
                    </S.GroupCard>
                  </S.Group>
                ))}
                <S.Banners>
                  <ValidationBanner />
                  <WeekendWarningBanner payrollModelId={payrun.payrollModelId} />
                </S.Banners>
              </S.Body>
            </FormProvider>
          </Modal.Body>
        }
      />
      <Modal.Slot
        name={ModalDialogSlot.Footer}
        render={
          <Modal.Footer
            btnPrimary={{
              onClickAsync: handleSubmit,
              label: 'Save changes',
              ariaLabel: 'Save milestone changes',
              type: ButtonType.Primary,
            }}
            btnTertiary={{
              onClick: handleCancel,
              label: 'Cancel',
              ariaLabel: 'Cancel edit milestones',
              type: ButtonType.Tertiary,
            }}
          />
        }
      />
    </Modal>
  );
}
